import styles from './Column.module.scss'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { gsap, Expo } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { formatCreateDate } from 'lib/functions'
import BasicTitle from 'components/commons/BasicTitle/BasicTitle'
import Button from 'components/commons/Button/Button'
import Head from 'next/head'

gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TweenVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Expo.easeOut,
}

interface Post {
  id: number
  title: {
    rendered: string
  }
  content: {
    rendered: string
  }
  date: string
  link: string
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string
    }>
    'wp:term'?: Array<Array<{ name: string }>> // カテゴリー用
  }
}

interface Props {
  tagType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

const Column: React.FC<Props> = ({ tagType = 'h3' }) => {
  const [posts, setPosts] = useState<Post[]>([])
  const router = useRouter()
  const isHomePage = router.pathname === '/'
  const isBrandPage = router.pathname === '/brand'

  // WP REST APIからデータを取得
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch('https://www.i3design.jp/in-pocket/wp-json/wp/v2/posts?_embed')
        const data: Post[] = await response.json()
        setPosts(data)
      } catch (error) {
        console.error('Error fetching posts:', error)
      }
    }

    fetchPosts()
  }, [])

  useEffect(() => {
    const tl = gsap.timeline()
    tl.from('.Column_read', tweenVars, 0.6)
      .from('.Column_box1', tweenVars, 0.9)
      .from('.Column_box2', tweenVars, 1.1)
      .from('.Column_box3', tweenVars, 1.4)
      .from('.Column_box4', tweenVars, 1.7)
      .from('.Column_bottom', tweenVars, 2.0)

    ScrollTrigger.create({
      animation: tl,
      trigger: '.Column_title',
      start: 'top 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  const list = posts.slice(0, 4).map((post, i) => (
    <div key={post.id} className={`${styles.columnitem} Column_box${i + 1}`}>
      <a href={post.link} rel="noopener">
        <figure className={styles.imgContainer}>
          <img
            src={post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '/images/default-thumbnail.jpg'}
            className={styles.image}
            alt={post.title.rendered}
          />
        </figure>
        <div className={styles.textwrap}>
          <span className={styles.day}>{`${formatCreateDate(post.date)}`}</span>
          <p className={styles.column_title}>{post.title.rendered}</p>
          <p className={styles.column_category}>
            {post._embedded?.['wp:term']?.[0]?.map((category) => category.name).join(', ') || '未分類'}
          </p>
        </div>
      </a>
    </div>
  ))

  const sectionTitle = {
    title: 'in-Pocket',
    sub_title: '自社ブログ',
  }

  return (
    <>
      <Head>
        <link rel="preload" href="/icon_external-link_black.svg" as="image" />
      </Head>
      <section className={`${styles.wrapper} column_wrapper ${isBrandPage ? styles.is_brand : ''}`}>
        <div className={styles.inner}>
          <div className={`${isHomePage ? '' : styles.ttlWrapper}`}>
            <BasicTitle sectionTitle={sectionTitle} tagType={tagType} />
            <div className={`${styles.heading} Column_read  ${isHomePage ? '' : styles.heading_other}`}>
              {isHomePage && (
                <div className={styles.heading_top}>
                  <div className={styles.heading_top_img}>
                    <img src="/images/logo/logo_in-pocket.png" alt="in-Pocket" />
                  </div>
                  <p className={styles.heading_top_txt}>
                    in-Pocketはアイスリーデザインのメンバーが発信しているオウンドメディアです。
                    <br />{' '}
                    ITビジネスで、「ひとつ時代の先を行く人」がポケットの中にとっておきたくなる、デザインとテクノロジーの話題をお伝えします。
                  </p>
                </div>
              )}
              <div className={`${styles.heading_btn} ${isHomePage ? '' : styles.heading_btn_other}`}>
                <Button text="in-Pocket" href="/in-pocket/" />
              </div>
            </div>
          </div>
          <div className={styles.columnwrap}>{list}</div>
          {isHomePage && (
            <div className={`${styles.bottom} Column_bottom`}>
              <p className={styles.bottom_txt}>その他、各種媒体でも情報発信していますのでぜひご覧ください。</p>
              <div className={styles.bottom_card}>
                <div className={styles.carditem}>
                  <div className={`${styles.carditem_inner} ${styles.carditem_inner_link}`}>
                    <a href="https://tech.i3design.jp/" target="_blank" rel="noopener">
                      <img src="/images/img/img_intro_tech.svg" alt="i3DESIGN エンジニア Tech Blog" />
                    </a>
                  </div>
                </div>
                <div className={styles.carditem}>
                  <div className={`${styles.carditem_inner} ${styles.carditem_inner_link}`}>
                    <a href="https://note.com/i3design_design/" target="_blank" rel="noopener">
                      <img src="/images/img/img_intro_note.svg" alt="i3DESIGN デザイナー note" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default Column
